/* eslint-disable valid-jsdoc */
/**
 * API Action Creators and related tokens and types.
 *
 * @module actions/Api
 */

import { ActionTypes, ApiEndpoints, ApiToken, ServiceHostTypes } from '../constants/ApiActions';

/**
 * Miscellaneous API actions
 */

export function sendServiceErrorToMiddleware(params, error) {
  return {
    type: ActionTypes.SEND_SERVICE_ERROR_TO_MIDDLEWARE,
    data: {
      action: {
        [ApiToken]: params,
      },
      error,
    },
  };
}

/**
 * Listener (User) API Action Creators
 */

/**
 * Action creator for sending a password reset email to the listener,
 * which includes a link that takes them to the reset password flow.
 *
 * @param {object} options - options to be passed as content to the request
 * @param {string} options.username - username to identify user
 * @param {string} options.redirectPath - If a redirectPath is specified,
 * the browser will be directed there after password reset
 * @returns {object} - return an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiEmailPassword({ username, redirectPath }) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.EMAIL_PASSWORD,
      content: {
        username,
        redirectPath,
      },
    },
    username,
  };
}

/**
 * Action creator for validating password reset token
 *
 * @param {string} resetToken - a password reset token that needs to be validated
 * @returns {object} - return an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiValidatePasswordResetToken(resetToken) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.VALIDATE_PASSWORD_RESET_TOKEN,
      content: {
        resetToken,
      },
    },
  };
}

/**
 * Action creator for updating registration information and/or the
 * explicit content filter. Options that are undefined will not be sent
 * in the API request.
 *
 * @param {object} options - options to be passed as content
 * @param {string} options.currentUsername - current username
 * @param {string} options.currentPassword - current password
 * @param {string} options.newUsername - username that should be set
 * @param {string} options.newPassword - password that should be set
 * @param {string} options.newZipCode - zip code that should be set
 * @param {string} options.newGender - gender that should be set
 * @param {string} options.newBirthYear - birth year that should be set
 * @param {string} options.newBirthMonth - birth month that should be set
 * @param {string} options.newBirthDay - birth day that should be set
 * @param {boolean} options.enableExplicitContentFilter - whether to enable
 * the explicit content filter for this user
 * @param {string} options.explicitPIN - PIN for setting explicit filter, must
 * be included if setting enableExplicitContentFilter
 * @returns {object} - return an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiUpdateAccount({
  currentUsername,
  currentPassword,
  newUsername,
  newPassword,
  newZipCode,
  newGender,
  newBirthYear,
  newBirthMonth,
  newBirthDay,
  enableExplicitContentFilter,
  explicitPIN,
}) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.UPDATE_ACCOUNT,
      content: {
        currentUsername,
        currentPassword,
        newUsername,
        newPassword,
        newZipCode,
        newGender,
        newBirthYear,
        newBirthMonth,
        newBirthDay,
        enableExplicitContentFilter,
        explicitPIN,
      },
    },
  };
}

/**
 * Action creator forUpdates one or more of the listener's privacy, notification,
 * Facebook auto-share and/or artist audio message settings.
 * Any properties not included in the request will remain unchanged.
 *
 * @param {object} options - options to be passed as content
 * @param {boolean} options.profilePrivate - whether user profile is private
 * @param {boolean} options.emailOptOut - whether to opt out of emails
 * @param {boolean} options.allowProfileComments - whether to allow comments on profile
 * @param {boolean} options.notifyOnComment - whether to notify on comment
 * @param {boolean} options.notifyOnFollow - whether to notify on follow
 * @param {boolean} options.artistPromoEmailsEnabled - whether user can get
 * artist promo emails.
 * @param {boolean} options.artistAudioMessagesEnabled - whether user can get
 * artist audio messages
 * @param {Array} options.artistMessageSettings - settings to be used for artist
 * messages. Contains objects that contain setting parameters (stationIds, and whether
 * to enable them).
 * @param {boolean} options.facebookAutoShareEnabled - whether FB auto share is enabled
 * @param {boolean} options.facebookAutoShareTrackPlay - whether to auto share
 * track plays to facebook
 * @param {boolean} options.facebookAutoShareLikes - whether to auto share likes
 * to facebook.
 * @param {string} options.facebookAutoShareFollows - whether to auto share follows
 * to facebook.
 * @param options.autoplayEnabled
 * @param options.birthYear
 * @param options.gender
 * @param options.highQualityStreamingEnabled
 * @param options.password
 * @param options.postalCode
 * @param options.seenWebPremiumWelcome
 * @param options.username
 * @param options.webClient2016OptedIn
 * @returns {object} - return an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiUpdateSettings({
  allowProfileComments,
  artistAudioMessagesEnabled,
  artistMessageSettings,
  artistPromoEmailsEnabled,
  autoplayEnabled,
  birthYear,
  emailOptOut,
  facebookAutoShareEnabled,
  facebookAutoShareFollows,
  facebookAutoShareLikes,
  facebookAutoShareTrackPlay,
  gender,
  highQualityStreamingEnabled,
  notifyOnComment,
  notifyOnFollow,
  password,
  postalCode,
  profilePrivate,
  seenWebPremiumWelcome,
  username,
  webClient2016OptedIn,
}) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.UPDATE_SETTINGS,
      content: {
        allowProfileComments,
        artistAudioMessagesEnabled,
        artistMessageSettings,
        artistPromoEmailsEnabled,
        autoplayEnabled,
        birthYear,
        emailOptOut,
        facebookAutoShareEnabled,
        facebookAutoShareFollows,
        facebookAutoShareLikes,
        facebookAutoShareTrackPlay,
        gender,
        highQualityStreamingEnabled,
        notifyOnComment,
        notifyOnFollow,
        password,
        postalCode,
        profilePrivate,
        seenWebPremiumWelcome,
        username,
        webClient2016OptedIn,
      },
    },
  };
}

/**
 * Action creator for getting the profile information for a listener.
 * Information will be returned for any listener, but only a subset of properties
 * is returned if the profile is set to "private". If a profile is private,
 * then only the owner of that profile will be returned full profile info.
 *
 * @param {string} webname - name of the profile to look up
 * @returns {object} - return an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiGetProfile(webname) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.GET_PROFILE,
      content: {
        webname,
      },
    },
  };
}

/**
 * Action creator for setting information on the profile page.
 *
 * @param {object} options - options to be passed as content to the request
 * @param {string} options.fullName - full name for profile
 * @param {string} options.homeTown - hometown for the profile
 * @param {string} options.school - school for the profile
 * @param {string} options.employer - employer for the profile
 * @param {string} options.bio - bio for the profile
 * @returns {object} - return an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiSetProfile({ fullName, homeTown, school, employer, bio }) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.SET_PROFILE,
      content: {
        fullName,
        homeTown,
        school,
        employer,
        bio,
      },
    },
  };
}

/**
 * Login API Action Creators
 */

/**
 * Action creator for indicating that the listener has seen a bar message
 * and 'dismissed' (closed) it.
 *
 * @param {string} messageKey - the key for the message
 * @returns {object} - return an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiAccountMessageDismissed(messageKey) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.ACCOUNT_MESSAGE_DISMISSED,
      content: {
        messageKey,
      },
    },
  };
}

/**
 * Music API Action Creators
 */

/**
 * Action creator that gets a list Genres Categories
 *
 * @returns {[{}]} - List of Categories entities
 */
export function ApiGetGenreCategories() {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.FETCH_GENRE_CATEGORIES,
      method: 'get',
    },
  };
}

/**
 * Action creator that gets a list Genre Stations for a given Genre Category
 *
 * @param categoryToken
 * @returns {[{}]} - List of Genre Stations
 */
export function ApiGetGenresByCategoryId(categoryToken) {
  return {
    categoryToken,
    [ApiToken]: {
      endpoint: ApiEndpoints.FETCH_GENRES_BY_CATEGORY_ID,
      method: 'get',
      params: {
        categoryToken,
      },
    },
  };
}

/**
 * Action creator that gets a list Genre Stations for a given Genre Category
 *
 * @param genreTokens
 * @returns {[{}]} - List of Genre Stations
 */
export function ApiGetGenresByToken(genreTokens) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.FETCH_GENRES_BY_CATEGORY_ID,
      method: 'get',
      params: {
        genreTokens,
      },
    },
  };
}

/**
 * Playlist API Action Creators
 */

// TODO: We still need an action creator for playlist/getContent. This is tied to Chronos V2 and still a work in progress.
// When it is available we should create an action creator for it here

/**
/**
 * Stations API Action Creators
 */

/**
 * getOrSetShuffleStations - Action creator for fetching or setting the shuffle station.
 *
 * @param  {Array[String]} stationIds - list of stationIds. If the argument is not passed
 * we will just get the shuffle station back with all the valid shuffle stations. If it is passed
 * then the shuffle station will set those as the stationsToShuffle
 * @returns {object} - an object that will get handled by the API middleware
 */
export function getOrSetShuffleStations(stationIds) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.GET_OR_SET_SHUFFLE_STATIONS,
      content: {
        stationIds,
      },
    },
  };
}

/**
 * Action creator that adds a seed to a station
 *
 * @param  {object} options - list of options passed into content
 * @param  {string} options.stationId - ID for station
 * @param  {string} options.musicId - musicID of seed to add
 * @returns {object} - an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiAddSeed({ stationId, musicId }) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.ADD_SEED,
      content: {
        stationId,
        musicId,
      },
    },
  };
}

/**
 * Action creator that deletes a seed from a station
 *
 * @param  {object} options - list of options passed into content
 * @param  {string} options.stationId - ID for station
 * @param  {string} options.musicId - musicID of seed to delete
 * @returns {object} - an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiDeleteSeed({ stationId, musicId }) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.DELETE_SEED,
      content: {
        stationId,
        musicId,
      },
    },
  };
}

/**
 * Action creator for getting narrative
 *
 * @param  {object} options - list of options passed into content
 * @param  {string} options.trackToken - token for the track
 * @param options.trackId
 * @param options.stationId
 * @param options.isAPSEnabled
 * @returns {object} - an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiGetNarrative({ trackToken, trackId, stationId, isAPSEnabled }) {
  return {
    updateEntitiesWith: { trackToken },
    [ApiToken]: {
      endpoint: ApiEndpoints.FETCH_NARRATIVE,
      content: {
        musicId: trackId,
        stationId,
      },
    },
    isAPSEnabled,
  };
}

/**
 * Action creator for getting positive feedback sorted from most recent to least
 *
 * @param  {object} options - list of options passed into content
 * @param  {string} options.pageSize - number of results to return in call. default 5 if unspecified, max 100
 * @param  {boolean} options.startIndex - starting index incrementing by 1 for each thumb
 * @param  {boolean} options.mostRecent - sorts feedback by recency
 * @param  {string} options.webname - user retrieving feedback
 * @returns {object} - an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiGetFeedback({ pageSize, startIndex, mostRecent, webname }) {
  return {
    mostRecent,
    webname,
    [ApiToken]: {
      endpoint: ApiEndpoints.GET_FEEDBACK,
      content: {
        pageSize,
        startIndex,
        webname,
        mostRecent,
      },
    },
  };
}

/**
 * Action creator for getting all feedback for a station
 *
 * @param  {object} options - list of options passed into content
 * @param  {string} options.pageSize - number of results to return in call. default 5 if unspecified, max 100
 * @param  {boolean} options.startIndex - starting index incrementing by 1 for each thumb
 * @param  {string} options.stationId - the station we want to get feedback for
 * @param  {boolean} options.positive - whether to retrieve positive or negative feedback
 * @param  {boolean} options.mostRecent - if a track needs to be prepended to the list
 * @returns {object} - an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiGetStationFeedback({ pageSize, startIndex, stationId, positive, mostRecent }) {
  return {
    stationId,
    mostRecent,
    [ApiToken]: {
      endpoint: ApiEndpoints.GET_STATION_FEEDBACK,
      content: {
        pageSize,
        startIndex,
        stationId,
        positive,
      },
    },
  };
}

/**
 * Action creator that flags current artist message as inappropiate
 *
 * @param  {string} options.artistMessageId - ID for artist message
 * @param  {string} options.artistUid - Uid for artist
 * @param artistMessageId
 * @param artistUid
 * @param reason
 * @param trackId
 * @returns {object} - an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiFlagMessage(artistMessageId, artistUid, reason, trackId) {
  return {
    updateEntitiesWith: {
      trackId,
    },
    [ApiToken]: {
      endpoint: ApiEndpoints.FLAG_MESSAGE,
      content: {
        artistMessageId,
        artistUid,
        reason,
      },
    },
  };
}

/**
 * Sends information to the server when the music has been paused or stopped due to user action.
 *
 * @param  {boolean} async - whether this request should be done asynchronously or not
 * in the case of window.onunload event we want to fire this action synchronously, since all
 * async XHR requests get flushed out on window.onunload.
 * @returns {object} - an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiPlaybackPaused(async = true) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.PLAYBACK_PAUSED,
      sync: !async,
    },
  };
}

/**
 * @param  {object} options - list of options passed into content
 * @param  {string} options.facebookId - facebookID for user
 * @param  {string} options.facebookName - facebookName of user
 * @param  {string} options.accessToken - accessToken for connecting to facebook
 * @param  {string} options.accessTokenExpires - the time when the accessToken expires
 * @returns {object} - an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiFacebookConnect({ facebookId, facebookName, accessToken, accessTokenExpires }) {
  return {
    data: {
      facebookId,
      facebookName,
      accessToken,
      accessTokenExpires,
    },
    [ApiToken]: {
      endpoint: ApiEndpoints.FACEBOOK_CONNECT,
      content: {
        facebookId,
        facebookName,
        accessToken,
        accessTokenExpires,
      },
    },
  };
}

/**
 * @returns {object} - an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiFacebookDisconnect() {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.FACEBOOK_DISCONNECT,
    },
  };
}

/**
 * @param {string} token - token for twitter oauth
 * @param {string} secret - secret for twitter oauth
 * @param {string} twitterHandle - screenName of user on twitter
 * @returns {object} - an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function ApiSetUserTokens({ token, secret, twitterHandle }) {
  return {
    data: {
      token,
      secret,
      twitterHandle,
    },
    [ApiToken]: {
      endpoint: ApiEndpoints.SET_TWITTER_TOKENS,
      content: {
        token,
        secret,
        twitterHandle,
      },
    },
  };
}

export function ApiGetRequestToken(query) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.GET_REQUEST_TOKEN,
      serviceHostType: ServiceHostTypes.TWITTER,
      query,
    },
  };
}

export function ApiGetAuthToken(params, data) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.GET_AUTH_TOKEN,
      serviceHostType: ServiceHostTypes.TWITTER,
      content: data,
      params,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    },
  };
}

export function ApiRegisterImpression({
  trackerUrl,
  urlType,
  adRequestMetaData,
  adToken,
  stationId,
  httpReferrer,
}) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.REGISTER_IMPRESSION,
      method: 'post',
      content: {
        trackerUrl,
        urlType,
        adRequestMetaData,
        adToken,
        stationId,
        httpReferrer,
      },
    },
  };
}

/**
 * Is currently only used to register the _start_ of video ad playback. Cannot
 * be issued until after the _duration_ value of the video ad can be determined.
 *
 * @param {string} creativeId The id passed in with the XML payload of the ad
 * @param {number} secondsPlayed Should always be -1
 * @param {number} adLength The _duration_ of the ad video content
 * @returns {object} The action object
 */
export function ApiRegisterVideoAdImpression({
  creativeId = '',
  secondsPlayed = -1,
  adLength = 0,
  httpReferrer,
}) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.REGISTER_VIDEO_AD_IMPRESSION,
      method: 'post',
      content: {
        creativeId,
        secondsPlayed,
        // for some reason only accepts integers
        adLength: Math.round(adLength),
        httpReferrer,
      },
    },
  };
}

export function ApiDeleteListener({ email, password, listenerId }) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.DELETE_LISTENER,
      content: {
        email,
        password,
        listenerId,
      },
    },
  };
}

export function ApiLogOutAllDevices({ password }) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.LOG_OUT_ALL_DEVICES,
      content: {
        password,
      },
    },
  };
}

export function ApiBrokenAd(params) {
  return {
    [ApiToken]: {
      endpoint: ApiEndpoints.BROKEN_AD,
      method: 'post',
      content: params,
    },
  };
}
